import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { ProjectModelEmbed } from '#components/cad-renderer/project-model-embed.js';
import { useProjectModelImageUrlResolver } from '#components/cad-renderer/use-project-model-image-url-resolver.js';
import { useTRPC } from '#src/trpc.js';
import {
	persistentQueryOptions,
	persistentQueryPolicies,
} from '#utils/persistent-query-cache/persistent-query-cache.js';
import { ModelRenderFrame } from './model-render-frame.js';

import type { DesignModels } from '@cadstrom/sigma-engine-shared';

export interface ProjectModelRenderProps {
	/** Organization the current route is scoped to. */
	orgName: string;
	/** Project whose model is rendered. */
	projectId: string;
	/** Revision (build) whose model is rendered. */
	revisionId: string;
	/** Design to display; defaults to the model's first schematic. */
	designId?: string;
	/** Initial zoom box, in model coordinates. */
	bbox?: DesignModels.ModelBoundingBox;
	/** Fallback title when the design has no name. */
	label?: string;
}

/**
 * Model render sourced from the revision's real project model: loads the raw
 * model (shared persistent cache with the design canvas) and renders the
 * requested design in a framed embed with a link to the design canvas.
 *
 * @param props - Component props.
 */
export function ProjectModelRender({ orgName, projectId, revisionId, designId, bbox, label }: ProjectModelRenderProps) {
	const trpc = useTRPC();
	const { data, isLoading, isError } = useQuery(
		trpc.project.getRawModel.queryOptions(
			{ projectId, buildId: revisionId },
			persistentQueryOptions(persistentQueryPolicies.projectModel),
		),
	);
	const imageUrlResolver = useProjectModelImageUrlResolver(projectId, revisionId);

	if (isLoading) {
		return <div className='my-2 h-80 w-full animate-pulse rounded-lg border border-gray-a4 bg-gray-a2' />;
	}

	if (isError || data === undefined) {
		return (
			<div className='my-2 rounded-lg border border-gray-a4 p-3 text-sm text-gray-11'>
				Design {label !== undefined && label !== '' ? `“${label}” ` : ''}could not be loaded.
			</div>
		);
	}

	const design = designId !== undefined ? data.projectModel.designs[designId] : undefined;
	const title = design?.designName ?? label ?? 'Design';

	return (
		<ModelRenderFrame
			title={title}
			action={
				<Link
					to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/design-canvas'
					params={{ orgName, projectId, revisionId }}
					search={{ designId }}
					className='text-xs whitespace-nowrap text-blue-11 hover:underline'
				>
					Open in design canvas
				</Link>
			}
		>
			<ProjectModelEmbed
				revisionId={revisionId}
				projectModel={data.projectModel}
				designId={designId}
				bbox={bbox}
				imageUrlResolver={imageUrlResolver}
				hideNavigationControls
				className='h-full w-full'
			/>
		</ModelRenderFrame>
	);
}
